import { PlusIcon } from "@heroicons/react/20/solid";
import {
    ActivityCategory,
    activityCategoryLabels,
    ActivityRecordType,
    activityRecordTypeLabels,
    asActivityRecordTypes,
    RegionalScope,
} from "@sp-crm/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { activityCategoryIcons } from "components/tasks/activity-category-icons";
import { invalidateTaskTypes } from "components/tasks/invalidate-task-types";
import { useAvailableActivityCategories } from "components/tasks/use-activity-categories";
import {
    ActivityTypeRow,
    useScopedActivityTypeRows,
} from "components/tasks/use-activity-type-rows";
import { DeleteButton, EditButton, RestoreButton } from "components/ui/action-button";
import { ButtonSelect, ButtonSelectOption } from "components/ui/button-select";
import { ErrorMessage } from "components/ui/error-message";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { Formik, FormikHelpers } from "formik";
import React, { useCallback } from "react";
import { useCrmMode } from "store/selectors/hooks";
import {
    homeCareClientsSettingsLabel,
    placementClientsSettingsLabel,
    recordTypesForCrmMode,
} from "util/crm-mode";
import {
    recordTypeSummary,
    RecordTypeUsage,
    removalConfirmation,
    removedRecordTypes,
} from "./record-type-usage";

const chipClassName =
    "inline-flex shrink-0 items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600";

export const activityTypesDescription =
    "Configure the activity types your team uses to do their work. Every type belongs to a category, which determines how completed activities are tracked, and applies to one or more record types.";

const byName = (a: ActivityTypeRow, b: ActivityTypeRow): number =>
    a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());

const taskTypeDisplayName = (taskType: ActivityTypeRow): string =>
    taskType.name.length > 0 ? taskType.name : "(unnamed)";

const archiveConfirmation: Record<RegionalScope["kind"], string> = {
    agencyWide:
        "Activities and tasks already assigned this type will keep it. It can no longer be selected for new work, and you can restore it from the Archived section at any time. Offices with their own activity types keep their own setting.",
    office: "Activities and tasks already assigned this type will keep it. It can no longer be selected for new work in this office, and you can restore it from the Archived section at any time.",
};

const archivedDescription: Record<RegionalScope["kind"], string> = {
    agencyWide:
        "Archived types cannot be used for new work, but existing activities and tasks keep them. Restoring returns a type to its category.",
    office: "Archived types cannot be used for new work in this office, but existing activities and tasks keep them. Activity types the agency adds later appear here until you restore them.",
};

const useInvalidateTaskTypes = (): (() => void) => {
    const queryClient = useQueryClient();
    return useCallback(() => {
        void invalidateTaskTypes(queryClient);
    }, [queryClient]);
};

interface ActivityTypeEditorProps {
    scope: RegionalScope;
    disabled: boolean;
}

export const ActivityTypeEditor: React.FC<ActivityTypeEditorProps> = props => {
    const { scope, disabled } = props;
    const { rows, error } = useScopedActivityTypeRows(scope);
    const availableRecordTypes = useAvailableRecordTypes();
    const availableCategories = useAvailableActivityCategories();
    if (error) {
        return (
            <ErrorMessage component="ActivityTypeEditor">
                <pre>{JSON.stringify(error, null, 2)}</pre>
            </ErrorMessage>
        );
    }
    if (rows === undefined) {
        return <Spinner />;
    }
    const taskTypes = rows.filter(taskType =>
        taskType.recordTypes.some(recordType =>
            availableRecordTypes.includes(recordType),
        ),
    );
    const archivedTaskTypes = taskTypes
        .filter(taskType => taskType.isArchived)
        .sort(byName);
    const activeTaskTypes = taskTypes
        .filter(taskType => !taskType.isArchived)
        .sort(byName);
    return (
        <div className="max-w-xl space-y-4">
            {availableCategories.map(category => (
                <CategorySection
                    key={category}
                    scope={scope}
                    disabled={disabled}
                    category={category}
                    taskTypes={activeTaskTypes.filter(
                        taskType => taskType.category === category,
                    )}
                />
            ))}
            {archivedTaskTypes.length > 0 ? (
                <div className="rounded bg-gray-50 p-4">
                    <h2 className="text-lg twoverride font-bold">Archived</h2>
                    <p className="mb-1 text-xs text-gray-500">
                        {archivedDescription[scope.kind]}
                    </p>
                    <ul className="divide-y divide-gray-200">
                        {archivedTaskTypes.map(taskType => (
                            <li key={taskType.id} className="py-2">
                                <TaskTypeRow
                                    scope={scope}
                                    disabled={disabled}
                                    taskType={taskType}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

interface CategorySectionProps {
    scope: RegionalScope;
    disabled: boolean;
    category: ActivityCategory;
    taskTypes: ActivityTypeRow[];
}

const CategorySection: React.FC<CategorySectionProps> = props => {
    const { scope, disabled, category, taskTypes } = props;
    const [isAdding, setIsAdding] = React.useState(false);
    const startAdding = useCallback(() => setIsAdding(true), []);
    const stopAdding = useCallback(() => setIsAdding(false), []);
    const CategoryIcon = activityCategoryIcons[category];
    return (
        <section className="rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2.5">
                <CategoryIcon className="h-5 w-5 text-gray-600" />
                <h3 className="text-sm font-semibold text-gray-900">
                    {activityCategoryLabels[category]}
                </h3>
            </div>
            <div className="px-3">
                <ul className="divide-y divide-gray-200">
                    {taskTypes.map(taskType => (
                        <li key={taskType.id} className="py-2">
                            <TaskTypeRow
                                scope={scope}
                                disabled={disabled}
                                taskType={taskType}
                            />
                        </li>
                    ))}
                    <li className="py-2">
                        {isAdding ? (
                            <TaskTypeRowNew
                                scope={scope}
                                category={category}
                                close={stopAdding}
                            />
                        ) : (
                            <SecondaryCompactButton
                                type="button"
                                disabled={disabled}
                                onClick={startAdding}>
                                <span className="inline-flex items-center gap-1">
                                    <PlusIcon className="size-3.5" aria-hidden />
                                    Add {activityCategoryLabels[category]} type
                                </span>
                            </SecondaryCompactButton>
                        )}
                    </li>
                </ul>
            </div>
        </section>
    );
};

interface TaskTypeRowProps {
    scope: RegionalScope;
    disabled: boolean;
    taskType: ActivityTypeRow;
}

interface TaskTypeEdit {
    name: string;
    recordTypes: ActivityRecordType[];
}

const useAvailableRecordTypes = (): ActivityRecordType[] => {
    const crmMode = useCrmMode();
    return recordTypesForCrmMode(crmMode);
};

const useRecordTypeLabels = (): Record<ActivityRecordType, string> => {
    const { mode } = useCrmMode();
    return {
        ...activityRecordTypeLabels,
        placement: placementClientsSettingsLabel(mode),
        homeCare: homeCareClientsSettingsLabel(mode),
    };
};

interface RecordTypeSelectProps {
    selected: ActivityRecordType[];
    onChange: (recordTypes: ActivityRecordType[]) => void;
}

const RecordTypeSelect: React.FC<RecordTypeSelectProps> = props => {
    const { selected, onChange } = props;
    const availableRecordTypes = useAvailableRecordTypes();
    const labels = useRecordTypeLabels();
    const options: ButtonSelectOption[] = availableRecordTypes.map(recordType => ({
        id: recordType,
        label: labels[recordType],
    }));
    const toggle = (id: string) => {
        const recordType = asActivityRecordTypes([id])[0];
        onChange(
            selected.includes(recordType)
                ? selected.filter(existing => existing !== recordType)
                : [...selected, recordType],
        );
    };
    return (
        <div>
            <div className="text-xs font-medium text-gray-600">Applies to</div>
            <ButtonSelect options={options} selectedIds={selected} onToggle={toggle} />
        </div>
    );
};

interface RecordTypeSummaryProps {
    recordTypes: ActivityRecordType[];
}

const RecordTypeSummary: React.FC<RecordTypeSummaryProps> = props => {
    const { recordTypes } = props;
    const availableRecordTypes = useAvailableRecordTypes();
    const labels = useRecordTypeLabels();
    return (
        <p className="text-xs text-gray-500">
            {recordTypeSummary(recordTypes, availableRecordTypes, labels)}
        </p>
    );
};

interface RecordTypeUsageState {
    usage: RecordTypeUsage;
    isPending: boolean;
}

const useRecordTypeUsage = (
    scope: RegionalScope,
    taskType: ActivityTypeRow,
): RecordTypeUsageState => {
    const trpcClient = trpc.useTRPC();
    const usageQuery = useQuery({
        ...trpcClient.taskType.recordTypeUsage.queryOptions({
            taskTypeId: taskType.id,
            scope,
        }),
    });
    return {
        usage: usageQuery.data
            ? { kind: "known", openTaskCounts: usageQuery.data.openTaskCounts }
            : { kind: "unknown" },
        isPending: usageQuery.isPending,
    };
};

interface TaskTypeEditFormProps {
    scope: RegionalScope;
    taskType: ActivityTypeRow;
    currentRecordTypes: ActivityRecordType[];
    isPending: boolean;
    onSave: (values: TaskTypeEdit) => void;
    onCancel: () => void;
}

const AgencyNameNote: React.FC<{ taskType: ActivityTypeRow }> = props => {
    const { taskType } = props;
    if (taskType.ownedByList || taskType.name === taskType.ownerName) {
        return null;
    }
    return <div className="text-xs text-gray-500">Agency name: {taskType.ownerName}</div>;
};

const TaskTypeEditForm: React.FC<TaskTypeEditFormProps> = props => {
    const { scope, taskType, currentRecordTypes, isPending, onSave, onCancel } = props;
    const { usage, isPending: isUsagePending } = useRecordTypeUsage(scope, taskType);
    const labels = useRecordTypeLabels();
    const submit = React.useCallback(
        async (values: TaskTypeEdit) => {
            const removed = removedRecordTypes(currentRecordTypes, values.recordTypes);
            const confirmation = removalConfirmation(
                taskTypeDisplayName(taskType),
                removed,
                usage,
                labels,
            );
            if (confirmation !== null) {
                const confirmed = await fancyConfirm(
                    confirmation.title,
                    confirmation.message,
                    "Yes, save",
                    "Cancel",
                    { confirmStyle: "question" },
                );
                if (!confirmed) {
                    return;
                }
            }
            onSave(values);
        },
        [currentRecordTypes, labels, onSave, taskType, usage],
    );
    return (
        <Formik
            initialValues={{ name: taskType.name, recordTypes: currentRecordTypes }}
            onSubmit={submit}>
            {formValues => (
                <form onSubmit={formValues.handleSubmit} className="space-y-2">
                    <div className="flex items-end space-x-1">
                        <Input
                            type="text"
                            label="Name"
                            name="name"
                            onChange={formValues.handleChange}
                            onBlur={formValues.handleBlur}
                            value={formValues.values.name}
                        />
                        {isPending || isUsagePending ? (
                            <div className="w-7 h-7">
                                <Spinner />
                            </div>
                        ) : (
                            <>
                                <PrimaryButton
                                    disabled={
                                        formValues.values.name.trim().length === 0 ||
                                        formValues.values.recordTypes.length === 0
                                    }
                                    type="submit">
                                    Save
                                </PrimaryButton>
                                <SecondaryButton type="button" onClick={onCancel}>
                                    Cancel
                                </SecondaryButton>
                            </>
                        )}
                    </div>
                    {!taskType.ownedByList &&
                    formValues.values.name !== taskType.ownerName ? (
                        <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                            <span>Agency name: {taskType.ownerName}</span>
                            <InteractiveTextLink
                                className="text-xs font-medium"
                                onClick={() =>
                                    formValues.setFieldValue("name", taskType.ownerName)
                                }>
                                Use agency name
                            </InteractiveTextLink>
                        </div>
                    ) : null}
                    <RecordTypeSelect
                        selected={formValues.values.recordTypes}
                        onChange={recordTypes =>
                            formValues.setFieldValue("recordTypes", recordTypes)
                        }
                    />
                </form>
            )}
        </Formik>
    );
};

export const TaskTypeRow: React.FC<TaskTypeRowProps> = props => {
    const { scope, disabled, taskType } = props;
    const [isEditing, setIsEditing] = React.useState(false);
    const trpcClient = trpc.useTRPC();
    const invalidateTaskTypes = useInvalidateTaskTypes();
    const updateTaskType = useMutation({
        ...trpcClient.taskType.updateTaskType.mutationOptions(),
        onSuccess: invalidateTaskTypes,
    });
    const isPending = updateTaskType.isPending;
    const taskTypeId = taskType.id;
    const archive = useCallback(async () => {
        const confirmed = await fancyConfirm(
            `Archive ${taskTypeDisplayName(taskType)}?`,
            archiveConfirmation[scope.kind],
            "Yes, archive",
            "Cancel",
            { confirmStyle: "question" },
        );
        if (!confirmed) {
            return;
        }
        updateTaskType.mutate({
            scope,
            taskTypeId,
            name: null,
            isArchived: true,
            recordTypes: null,
        });
    }, [scope, taskType, taskTypeId, updateTaskType]);
    const restore = useCallback(() => {
        updateTaskType.mutate({
            scope,
            taskTypeId,
            name: null,
            isArchived: false,
            recordTypes: null,
        });
    }, [scope, taskTypeId, updateTaskType]);
    const enableEditing = useCallback(() => {
        setIsEditing(true);
    }, []);
    const { reset: resetUpdate } = updateTaskType;
    const cancelEditing = useCallback(() => {
        resetUpdate();
        setIsEditing(false);
    }, [resetUpdate]);
    const currentRecordTypes = taskType.recordTypes;
    const save = React.useCallback(
        (values: TaskTypeEdit) => {
            updateTaskType.mutate(
                {
                    scope,
                    taskTypeId,
                    name: values.name,
                    isArchived: null,
                    recordTypes: values.recordTypes,
                },
                { onSuccess: cancelEditing },
            );
        },
        [cancelEditing, scope, taskTypeId, updateTaskType],
    );
    const saveFailed = updateTaskType.isError ? (
        <InlineBanner type="error">
            The change could not be saved. Please try again.
        </InlineBanner>
    ) : null;
    if (isEditing) {
        return (
            <div className="space-y-2">
                <TaskTypeEditForm
                    scope={scope}
                    taskType={taskType}
                    currentRecordTypes={currentRecordTypes}
                    isPending={isPending}
                    onSave={save}
                    onCancel={cancelEditing}
                />
                {saveFailed}
            </div>
        );
    }
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 space-y-0.5">
                    <div className="flex min-w-0 items-center gap-2">
                        <div className="truncate">{taskTypeDisplayName(taskType)}</div>
                        {taskType.ownedByList ? null : (
                            <span className={chipClassName}>Agency</span>
                        )}
                        {taskType.isArchived ? (
                            <span className={chipClassName}>
                                {activityCategoryLabels[taskType.category]}
                            </span>
                        ) : null}
                    </div>
                    <AgencyNameNote taskType={taskType} />
                    <RecordTypeSummary recordTypes={currentRecordTypes} />
                </div>
                <div className="flex shrink-0 items-center space-x-2">
                    <EditButton
                        onClick={enableEditing}
                        disabled={disabled}
                        backgroundColor={"bg-white"}
                    />
                    {isPending ? (
                        <div className="w-7 h-7">
                            <Spinner />
                        </div>
                    ) : taskType.isArchived ? (
                        <RestoreButton
                            onClick={restore}
                            disabled={disabled}
                            backgroundColor={"bg-white"}
                        />
                    ) : (
                        <DeleteButton
                            onClick={archive}
                            disabled={disabled}
                            backgroundColor={"bg-white"}
                            label="Archive"
                        />
                    )}
                </div>
            </div>
            {saveFailed}
        </div>
    );
};

interface TaskTypeRowNewProps {
    scope: RegionalScope;
    category: ActivityCategory;
    close: () => void;
}

export const TaskTypeRowNew: React.FC<TaskTypeRowNewProps> = props => {
    const { scope, category, close } = props;
    const trpcClient = trpc.useTRPC();
    const invalidateTaskTypes = useInvalidateTaskTypes();
    const initialRecordTypes = useAvailableRecordTypes();
    const createTaskType = useMutation({
        ...trpcClient.taskType.createTaskType.mutationOptions(),
        onSuccess: invalidateTaskTypes,
    });
    const save = React.useCallback(
        (values: TaskTypeEdit, formik: FormikHelpers<TaskTypeEdit>) => {
            createTaskType.mutate(
                {
                    scope,
                    name: values.name,
                    category,
                    recordTypes: values.recordTypes,
                },
                {
                    onSuccess: () => {
                        formik.resetForm();
                        close();
                    },
                    onError: () => {
                        formik.setSubmitting(false);
                    },
                },
            );
        },
        [createTaskType, scope, category, close],
    );
    return (
        <Formik
            initialValues={{ name: "", recordTypes: initialRecordTypes }}
            onSubmit={save}>
            {formValues => (
                <form onSubmit={formValues.handleSubmit} className="space-y-2">
                    <div className="flex items-end space-x-1">
                        <Input
                            type="text"
                            label={`New ${activityCategoryLabels[category]} type`}
                            name="name"
                            autoFocus
                            onChange={formValues.handleChange}
                            onBlur={formValues.handleBlur}
                            value={formValues.values.name}
                        />
                        <PrimaryButton
                            disabled={
                                createTaskType.isPending ||
                                formValues.values.name.trim().length === 0 ||
                                formValues.values.recordTypes.length === 0
                            }
                            type="submit">
                            Create
                        </PrimaryButton>
                        <SecondaryButton type="button" onClick={close}>
                            Cancel
                        </SecondaryButton>
                    </div>
                    <RecordTypeSelect
                        selected={formValues.values.recordTypes}
                        onChange={recordTypes =>
                            formValues.setFieldValue("recordTypes", recordTypes)
                        }
                    />
                    {createTaskType.isError ? (
                        <InlineBanner type="error">
                            The activity type could not be created. Please try again.
                        </InlineBanner>
                    ) : null}
                </form>
            )}
        </Formik>
    );
};
